import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { BACKEND_URL } from '../config';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const { setUser, checkAuth } = useContext(AuthContext);
  const navigate = useNavigate();

  // Auto-login con magic link si la URL tiene ?token=
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) return;

    const doMagicLogin = async () => {
      setMagicLoading(true);
      try {
        const response = await axios.post(
          `${API}/auth/magic-login`,
          { token },
          { withCredentials: true }
        );
        // Guardar token en localStorage como respaldo si las cookies se borran (iOS Safari)
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        await checkAuth();
        toast.success(`¡Bienvenido/a ${response.data.user.name}!`);
        const userRole = response.data.user.role;
        if (userRole === 'administrativo') navigate('/dashboard');
        else if (userRole === 'asistente') navigate('/asistente');
        else if (userRole === 'modelo') navigate('/modelo');
        else navigate('/dashboard');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Link inválido o expirado');
        // Limpiar el token de la URL
        window.history.replaceState({}, document.title, '/login');
      } finally {
        setMagicLoading(false);
      }
    };
    doMagicLogin();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { username, password },
        { withCredentials: true }
      );
      
      // Guardar token en localStorage como respaldo si las cookies se borran (iOS Safari)
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }

      await checkAuth();
      toast.success('¡Bienvenido!');
      
      // Redirect based on role
      const userRole = response.data.user.role;
      if (userRole === 'administrativo') {
        navigate('/dashboard');
      } else if (userRole === 'empleado') {
        navigate('/empleado');
      } else if (userRole === 'asistente') {
        navigate('/asistente');
      } else if (userRole === 'modelo') {
        navigate('/modelo');
      } else {
        // Fallback - redirect to dashboard for any other role
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
      {magicLoading && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" data-testid="magic-link-loading">
          <div className="bg-gray-800 p-8 rounded-2xl border border-purple-500/30 text-center">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white font-medium">Procesando link de acceso...</p>
            <p className="text-gray-400 text-sm mt-2">Ingresando automáticamente</p>
          </div>
        </div>
      )}
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">JG Models Agency</h1>
          <p className="text-gray-400">Portal de Empleados</p>
        </div>

        {/* Login Card */}
        <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-6">Iniciar Sesión</h2>
          
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Usuario
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ingresa tu usuario"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingresa tu contraseña"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-purple-500/50"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              ← Volver al inicio
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;